/**
 * UserStreamManager - Manages single user-level WebSocket connection
 * 
 * Handles all real-time updates for the current user including:
 * - Chat streaming
 * - Chat status updates  
 * - Chat completion events
 * - System notifications (future)
 * 
 * Architecture:
 * - One WebSocket connection per browser tab
 * - All messages filtered by chat_id on client side
 * - Automatic reconnection with exponential backoff
 * - Handler subscription system for different message types
 * - Visibility-based state reconciliation
 * - Token refresh mechanism to prevent expired token issues
 */

class UserStreamManager {
  constructor() {
    this.connection = null;
    this.getTokenCallback = null;  // Callback to get fresh token
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity;  // Never give up — server restarts can take minutes
    this.reconnectDelay = 1000;
    this.shouldReconnect = true;
    this.heartbeatInterval = null;
    this.connectionTimeout = null;  // Timeout to reset stuck isConnecting state

    // Message type -> Set of handler functions
    this.messageHandlers = new Map();

    // Connection state
    this.isConnecting = false;
    this.connectionId = Math.random().toString(36).substring(7);

    // WS-dup diagnosis (2026-08-28, duplicate-streamed-lines bug): per-socket ids +
    // a short recent-event ring so a duplicated delivery self-identifies its layer
    // in the console (see handleMessage). Also exposes the singleton for console
    // inspection: __ccUserStream.getDebugInfo().
    this.socketSeq = 0;
    this._recentEvents = [];
    if (typeof window !== 'undefined') {
      if (window.__ccUserStream && window.__ccUserStream !== this) {
        // Two manager instances = the module was bundled twice — every subscriber
        // would attach to one while sockets live on both. Loud and unmissable.
        console.error('[cc-ws-dup] SECOND UserStreamManager instance constructed — duplicate module in bundle!');
      }
      window.__ccUserStream = this;
    }

    // Visibility tracking
    this.lastVisibleTimestamp = Date.now();
    this.visibilityChangeHandler = null;

    // Multi-callback support for reconciliation
    this.reconciliationCallbacks = new Set();

    // Setup visibility listener
    this.setupVisibilityListener();
  }

  /**
   * Setup visibility change listener for tab focus/blur detection
   */
  setupVisibilityListener() {
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // Tab hidden - record timestamp
        this.lastVisibleTimestamp = Date.now();
      } else {
        // Tab visible again - check duration
        const hiddenDuration = Date.now() - this.lastVisibleTimestamp;

        // If hidden for >30s, trigger reconciliation
        if (hiddenDuration > 30000) {
          this.triggerReconciliation('visibility_return');
        }

        // If WebSocket disconnected, try to reconnect
        if (!this.isConnected() && this.getTokenCallback) {
          // Reset stuck isConnecting state if needed
          if (this.isConnecting) {
            console.warn('[UserStream] Resetting stuck isConnecting state on visibility change');
            this.isConnecting = false;
          }
          this.connect();
        }
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Set callback for getting fresh auth token
   * @param {Function} callback - Async function that returns fresh token
   */
  setTokenCallback(callback) {
    this.getTokenCallback = callback;
  }

  /**
   * Get fresh token using callback
   * @returns {Promise<string>} Fresh Firebase ID token
   */
  async getFreshToken() {
    if (!this.getTokenCallback) {
      throw new Error('[UserStream] Token callback not set');
    }

    const token = await this.getTokenCallback();
    return token;
  }

  /**
   * Add callback for state reconciliation
   * @param {Function} callback - Function to call when reconciliation is needed
   * @returns {Function} Unsubscribe function
   */
  addReconciliationCallback(callback) {
    this.reconciliationCallbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.reconciliationCallbacks.delete(callback);
    };
  }

  /**
   * Trigger state reconciliation for all registered callbacks
   * @param {string} reason - Reason for reconciliation (visibility_return, reconnection, etc.)
   */
  triggerReconciliation(reason) {
    if (this.reconciliationCallbacks.size === 0) {
      return;
    }

    this.reconciliationCallbacks.forEach(callback => {
      try {
        callback(reason);
      } catch (error) {
        console.error('[UserStream] Reconciliation callback error:', error);
      }
    });
  }

  /**
   * Ensure WebSocket is connected before critical operations.
   * Returns immediately if already connected (zero overhead).
   * If disconnected, attempts reconnection and waits up to timeoutMs.
   * 
   * @param {number} timeoutMs - Maximum time to wait for connection (default 3000ms)
   * @returns {Promise<boolean>} - true if connected, false if timed out (but doesn't block user action)
   */
  async ensureConnected(timeoutMs = 3000) {
    // Fast path: already connected
    if (this.isConnected()) {
      return true;
    }

    // Not connected - attempt reconnection
    if (!this.getTokenCallback) {
      console.warn('[UserStream] Cannot ensure connection: no token callback');
      return false;
    }

    // Reset stuck state if needed
    if (this.isConnecting) {
      console.warn('[UserStream] Resetting stuck isConnecting state in ensureConnected');
      this.isConnecting = false;
      // Clear any existing connection timeout
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
    }

    // Attempt connection
    this.connect();

    // Wait for connection with timeout
    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.isConnected()) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (Date.now() - startTime > timeoutMs) {
          clearInterval(checkInterval);
          console.warn('[UserStream] ensureConnected timed out after', timeoutMs, 'ms');
          resolve(false);  // Don't block user - let them send anyway
        }
      }, 50);  // Check every 50ms
    });
  }

  /**
   * Connect to user WebSocket stream with fresh token
   */
  async connect() {
    // Prevent multiple concurrent connection attempts
    if (this.isConnecting) {
      return;
    }

    // If already connected, no need to reconnect
    if (this.connection && this.connection.readyState === WebSocket.OPEN) {
      return;
    }

    // Close any stale connection that's not in OPEN state.
    // Detach its handlers FIRST (orphaned-socket fix, 2026-08-28): a discarded
    // socket's late onclose used to run `this.connection = null` unconditionally,
    // clobbering the reference to the healthy replacement socket — reconnect()
    // then opened a THIRD socket while the replacement stayed alive with a live
    // onmessage, so every WS event was handled twice (duplicate streamed lines
    // and duplicate sync-update history entries).
    if (this.connection && this.connection.readyState !== WebSocket.OPEN) {
      const stale = this.connection;
      stale.onopen = stale.onmessage = stale.onerror = stale.onclose = null;
      try {
        stale.close();
      } catch (e) {
        // Ignore close errors on stale connections
      }
      this.connection = null;
    }

    if (!this.getTokenCallback) {
      console.error('[UserStream] Cannot connect: token callback not set');
      return;
    }

    this.isConnecting = true;
    this.shouldReconnect = true;

    // Set up connection timeout to reset stuck isConnecting state
    this.connectionTimeout = setTimeout(() => {
      if (this.isConnecting) {
        console.warn('[UserStream] Connection timeout (10s), resetting state');
        this.isConnecting = false;
        // Close any stale connection — detach handlers first (see the
        // orphaned-socket note in connect(); a discarded socket must be inert).
        if (this.connection && this.connection.readyState !== WebSocket.OPEN) {
          const stale = this.connection;
          stale.onopen = stale.onmessage = stale.onerror = stale.onclose = null;
          try {
            stale.close();
          } catch (e) {
            // Ignore close errors
          }
          this.connection = null;
          // The detached socket can no longer fire the onclose reconnect path,
          // so schedule the retry explicitly — a stuck-connecting tab must
          // still recover on its own.
          if (this.shouldReconnect && this.getTokenCallback) {
            this.reconnect();
          }
        }
      }
    }, 10000);

    try {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = process.env.NODE_ENV === 'production'
        ? window.location.host
        : 'localhost:8000';

      // CASA 2.1.1: never put bearer tokens in URLs — they land in server access logs.
      // In prod the HttpOnly session cookie rides the same-origin wss:// handshake
      // automatically and the backend verifies it (same path as every API request).
      // Dev keeps the legacy ?token= param (FE :3000 -> BE :8000 direct connect).
      let wsUrl = `${wsProtocol}//${wsHost}/api/cellcog/ws/user/stream`;
      if (process.env.NODE_ENV !== 'production') {
        const token = await this.getFreshToken();
        wsUrl += `?token=${token}`;
      }

      // ORPHANED-SOCKET FIX (2026-08-28): bind every handler to THIS socket via a
      // local reference and guard each one with `this.connection !== socket`.
      // Events from a superseded socket must never mutate manager state or process
      // messages — previously a stale socket's late onclose clobbered the healthy
      // connection reference (spawning a third socket via reconnect()) while its
      // still-attached onmessage kept firing, double-processing every stream event.
      const socket = new WebSocket(wsUrl);
      socket.__ccId = ++this.socketSeq;
      this.connection = socket;
      console.info(`[UserStream] socket#${socket.__ccId} connecting (manager ${this.connectionId})`);

      socket.onopen = () => {
        if (this.connection !== socket) return; // superseded — ignore

        // Clear connection timeout on successful connection
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }

        this.isConnecting = false;

        // Track if this was a reconnection
        const wasReconnection = this.reconnectAttempts > 0;
        this.reconnectAttempts = 0;

        this.startHeartbeat();

        // Trigger reconciliation after successful reconnection
        if (wasReconnection) {
          this.triggerReconciliation('reconnection');
        }
      };

      socket.onmessage = (event) => {
        if (this.connection !== socket) return; // superseded — ignore

        // Handle pong response
        if (event.data === 'pong') {
          return;
        }

        // Parse and handle message
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message, socket.__ccId);
        } catch (error) {
          console.error('[UserStream] Failed to parse message:', error, event.data);
        }
      };

      socket.onerror = (error) => {
        if (this.connection !== socket) return; // superseded — ignore

        console.error(`[UserStream] Error (id: ${this.connectionId}):`, error);
        // Clear connection timeout on error
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        this.isConnecting = false;
      };

      socket.onclose = (event) => {
        console.info(`[UserStream] socket#${socket.__ccId} closed (was current: ${this.connection === socket})`);
        if (this.connection !== socket) return; // superseded — must not clobber state or reconnect

        // Clear connection timeout on close
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }

        this.connection = null;
        this.isConnecting = false;

        if (this.shouldReconnect && this.getTokenCallback) {
          this.reconnect();
        }
      };

    } catch (error) {
      console.error(`[UserStream] Failed to get token or create WebSocket:`, error);
      // Clear connection timeout on error
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      this.isConnecting = false;

      // Try to reconnect if token fetch failed
      if (this.shouldReconnect && this.getTokenCallback) {
        this.reconnect();
      }
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    this.shouldReconnect = false;

    // Clear connection timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    if (this.connection) {
      this.connection.close();
      this.connection = null;
    }

    this.getTokenCallback = null;
    this.reconnectAttempts = 0;
    this.isConnecting = false;

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    // Remove visibility listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  /**
   * Reconnect with exponential backoff and fresh token
   */
  reconnect() {
    if (!this.getTokenCallback) {
      console.error('[UserStream] Cannot reconnect: no token callback set');
      return;
    }

    this.reconnectAttempts++;
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, then cap at 30s forever
    const delay = Math.min(30000, this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1));


    setTimeout(async () => {
      if (this.shouldReconnect && this.getTokenCallback) {
        await this.connect();
      }
    }, delay);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    // Clear any existing heartbeat interval
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.connection && this.connection.readyState === WebSocket.OPEN) {
        this.connection.send('ping');
      } else {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    }, 30000); // 30 seconds
  }

  /**
   * Subscribe to a specific message type
   * 
   * @param {string} messageType - Type of message (CHAT_STREAM_CHUNK, etc.)
   * @param {Function} handler - Function to call with (data, timestamp)
   * @returns {Function} Unsubscribe function
   * 
   * Example:
   *   const unsubscribe = UserStreamManager.subscribe('CHAT_STREAM_CHUNK', (data, timestamp) => {
   *     console.log('Received chunk:', data);
   *   });
   *   
   *   // Later: unsubscribe();
   */
  subscribe(messageType, handler) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set());
    }

    this.messageHandlers.get(messageType).add(handler);


    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(messageType);
      if (handlers) {
        handlers.delete(handler);

        if (handlers.size === 0) {
          this.messageHandlers.delete(messageType);
        }
      }
    };
  }

  /**
   * Handle incoming WebSocket message
   * @param {Object} message - Parsed message { type, timestamp, data }
   */
  handleMessage(message, socketId) {
    const { type, timestamp, data } = message;

    // WS-dup detector (2026-08-28, duplicate-streamed-lines diagnosis): a byte-identical
    // CHAT_STREAM_CHUNK arriving twice within 3s is a delivery-layer duplicate. The log
    // names the layer:
    //   - two different socket ids  -> two live sockets in THIS tab (orphaned connection)
    //   - the same socket id twice  -> the server sent it twice (Redis/WS backend layer)
    //   - detector SILENT while the UI still shows duplicates -> render/DOM layer, not delivery
    // Trimmed-length filter keeps legit repeats (blank lines, separators) out.
    if (type === 'CHAT_STREAM_CHUNK' && data && typeof data.text === 'string' && data.text.trim().length > 10) {
      try {
        const key = `${data.chat_id}|${data.message_type}|${data.text}`;
        const now = Date.now();
        this._recentEvents = this._recentEvents.filter((e) => now - e.t < 3000).slice(-80);
        const prev = this._recentEvents.find((e) => e.key === key);
        if (prev) {
          const via = prev.socketId !== socketId
            ? `sockets #${prev.socketId} and #${socketId} — TWO LIVE SOCKETS in this tab (orphaned connection)`
            : `socket #${socketId} twice — SERVER-SIDE double delivery`;
          console.error(
            `[cc-ws-dup] duplicate ${data.message_type} chunk ${now - prev.t}ms apart via ${via} (manager ${this.connectionId})`,
            data.text.slice(0, 80),
          );
        }
        this._recentEvents.push({ key, t: now, socketId });
      } catch (e) {
        // Diagnosis must never break message handling.
      }
    }

    // Call registered handlers for this message type
    const handlers = this.messageHandlers.get(type);
    if (handlers && handlers.size > 0) {
      handlers.forEach(handler => {
        try {
          handler(data, timestamp);
        } catch (error) {
          console.error(`[UserStream] Handler error for ${type}:`, error);
        }
      });
    } else {
      console.warn(`[UserStream] No handlers registered for message type: ${type}`);
    }
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  isConnected() {
    return this.connection && this.connection.readyState === WebSocket.OPEN;
  }

  /**
   * Get debug information
   * @returns {Object} Debug info
   */
  getDebugInfo() {
    return {
      connected: this.isConnected(),
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      shouldReconnect: this.shouldReconnect,
      hasTokenCallback: !!this.getTokenCallback,
      connectionId: this.connectionId,
      lastVisibleTimestamp: this.lastVisibleTimestamp,
      reconciliationCallbackCount: this.reconciliationCallbacks.size,
      handlerCounts: Array.from(this.messageHandlers.entries()).map(([type, handlers]) => ({
        type,
        count: handlers.size
      }))
    };
  }
}

// Export singleton instance
export default new UserStreamManager();
